import _ from 'lodash';
import AbstractService from 'services/AbstractService';
import ProcessService from 'services/ProcessService';
import ProcessUtils from 'utils/ProcessUtils';
import TabsService from 'services/TabsService';

/**
 * Provides remote services for DataExplorer
 */
class DataExplorerService extends AbstractService {
    constructor() {
        super();
        this.DATASTREAM_RELOAD = 'DATASTREAM_RELOAD';
        this.DATASTREAMS_EXPLORE_START = 'DATASTREAMS_EXPLORE_START';
        this.DATASTREAMS_EXPLORE_SUCCESS = 'DATASTREAMS_EXPLORE_SUCCESS';
        this.DATASTREAMS_EXPLORE_ERROR = 'DATASTREAMS_EXPLORE_ERROR';
        this.DATASTREAMS_POLL_START = 'DATASTREAMS_POLL_START';
        this.DATASTREAMS_POLL_SUCCESS = 'DATASTREAMS_POLL_SUCCESS';
        this.DATASTREAMS_POLL_ERROR = 'DATASTREAMS_POLL_ERROR';
        this.baseUrl = process.env.REACT_APP_BASE_URL;
        this.intervalId = null;
    }

    getInitialState() {
        return {
            pendingRequests: {},
            requestsToCancel: {}
        };
    }

    getStoreKey() {
        return 'dataExplorer';
    }

    getReducer() {
        return (state = this.getInitialState(), action) => {
            let newState = state;

            switch (action.type) {
                case ProcessService.PROCESS_DELETE_SUCCESS:
                    newState = this._processDeleteSuccess(action, state);
                    break;

                case TabsService.TAB_CLOSE:  // when tab closes, we have to wipe the cache
                    newState = this._tabClose(action, state);
                    break;

                case ProcessService.PROCESS_RENAME:
                    newState = this._processRename(action, state);
                    break;

                case ProcessService.PROCESS_UNCACHE:
                    // For now, don't clear the explored data cache when you close a process so it can be shared
                    // between wizard and regular PCM
                    //newState = this._processUncache(action, state);
                    break;

                case this.DATASTREAM_RELOAD:
                    newState = this._datastreamReload(action, state);
                    break;

                case this.DATASTREAMS_EXPLORE_SUCCESS:
                    newState = this._datastreamsExploreSuccess(action, state);
                    break;

                case this.DATASTREAMS_EXPLORE_ERROR:
                    newState = this._datastreamsExploreError(action, state);
                    break;

                case this.DATASTREAMS_POLL_SUCCESS:
                    newState = this._datastreamsPollSuccess(action, state);
                    break;

                case this.DATASTREAMS_POLL_ERROR:
                    newState = this._datastreamsPollError(action, state);
                    break;

                default:  // stupid linting rule requires a default case
                    break;
            }
            return newState;
        };
    }

    getEmptyDataObject() {
        return {
            exploredFiles: [],
            currentData: {},
            error: null,
            status: null
        };
    }

    /**
     * Check if data is currently being explored for the given process
     * @param process
     */
    isDataBeingExplored(processId) {
        let exploring = false;
        const exploredDatastreams = this.getExploredDatastreams(processId);
        if (exploredDatastreams) {
            Object.values(this.getExploredDatastreams(processId)).forEach(exploredDatastream => {
                if (exploredDatastream.status === 'Loading') {
                    exploring = true;
                }
            });
        }
        return exploring;
    }

    /**
     * Get datastreams that aren't already being explored for the given process.
     * @param process
     * @param checkEvenIfNoTransformParameters
     */
    getDatastreamsThatNeedExploring(process) {
        const datastreamsToExplore = {};
        const datastreams = {};

        // Only can explore if this process has variable retrieval
        if (!_.isEmpty(process.variable_retrieval)) {

            // Add all datastreams and variables in the process if they have not already been explored
            // We have to check each variable, because a new variable may have been added to the process that
            // has not been explored yet
            ProcessUtils.visitAllInputDatasetRules(process, (rule, inputDataset) => {
                const datastreamName = rule.datastream_name;

                if (datastreamName) {
                    const explorerState = this.getExploredDatastream(process.name, datastreamName);

                    // If this datastream already failed or it is currently loading, then skip it
                    if (explorerState.status !== 'Error' && explorerState.status !== 'Loading') {
                        let variablesExplored = null;
                        if (explorerState.currentData && explorerState.currentData.variables) {
                            variablesExplored = explorerState.currentData.variables;
                        }

                        if (!datastreams[datastreamName]) {
                            datastreams[datastreamName] = {};
                        }

                        Object.values(rule.variable_mappings).forEach(inputVariableNames => {
                            inputVariableNames.forEach(variableName => {
                                // first check if this variable has already been explored
                                if (!variablesExplored || (variablesExplored && !variablesExplored[variableName])) {
                                    // we need to include this variable because it hasn't been explored yet
                                    datastreams[datastreamName][variableName] = true;
                                } else {
                                    // we already explored this variable, so don't include it
                                    datastreams[datastreamName][variableName] = false;
                                }
                            });
                        });
                    }
                }
            });

            // Filter out datastreams with no variables that need exploring
            Object.keys(datastreams).forEach(datastreamName => {
                const variables = datastreams[datastreamName];
                let includeDatastream = false;
                Object.values(variables).forEach(includeVariable => {
                    if (includeVariable) {
                        includeDatastream = true;
                    }
                });
                if (includeDatastream) {
                    datastreamsToExplore[datastreamName] = variables;
                }
            });
        }
        return datastreamsToExplore;
    }

    /**
     * Only returns the current data.  If there is no current data, returns empty object
     * @param datastreamName
     * @returns {*}
     */
    getCurrentData(processId, datastreamName) {
        const exploredData = this.getExploredDatastream(processId, datastreamName);
        return exploredData ? exploredData.currentData : null;
    }

    getPendingRequest(processId, datastreamName, state) {
        let pendingRequest = null;
        if (state.pendingRequests[processId] && state.pendingRequests[processId][datastreamName]) {
            pendingRequest = state.pendingRequests[processId][datastreamName];
        }
        return pendingRequest;
    }

    cancelRequest(processId, pendingRequest, newState) {
        newState.requestsToCancel[processId] = [...newState.requestsToCancel[processId]];
        newState.requestsToCancel[processId].push(pendingRequest);
    }

    removeCancelledRequests(processId, cancelledRequests, newState) {

        if (newState.requestsToCancel[processId]) {
            newState.requestsToCancel[processId] = newState.requestsToCancel[processId].reduce((accumulator, currentValue) => {
                if (cancelledRequests.indexOf(currentValue) === -1) {
                    accumulator.push(currentValue);
                }
                return accumulator;
            }, []);
        }
        return newState;
    }

    removePendingRequests(processId, datastreamNames, newState) {
        newState.pendingRequests[processId] = Object.assign({}, newState.pendingRequests[processId]);
        datastreamNames.forEach(datastreamName => {
            delete newState.pendingRequests[processId][datastreamName];
        });
        return newState;
    }

    /**
     * Create a null-safe object.  If the value is already in the store, it will make a shallow copy.  Otherwise,
     * it returns an empty object.
     * @param action
     * @param state
     * @returns {*}
     * @private
     */
    getExploredDatastream(processId, datastreamName, state) {
        if (!state) {
            state = this.getMyState();
        }

        let dataObject = null;

        if (state[processId] && !_.isEmpty(state[processId][datastreamName]) && state[processId][datastreamName].status !== 'Loading') {
            dataObject = Object.assign({}, state[processId][datastreamName]);
        } else {
            dataObject = this.getEmptyDataObject();
        }
        return dataObject;
    }

    getExploredDatastreams(processId, state) {
        if (!state) {
            state = this.getMyState();
        }
        return state[processId];
    }

    exploreDatastreams(processId, process, requests) {
        this.dispatch({
            type: this.DATASTREAMS_EXPLORE_START,
            processId,
            process,
            requests
        });
    }

    hasPendingRequestsForProcess(processId, completedDatastreamNames) {
        let hasRequests = false;
        const state = this.getMyState();

        if (state.pendingRequests[processId]) {
            Object.keys(state.pendingRequests[processId]).forEach(datastreamName => {
                if (completedDatastreamNames.indexOf(datastreamName) === -1) {
                    hasRequests = true;
                }
            });

        }
        return hasRequests;
    }

    hasPendingRequests(state) {
        let hasRequests = false;
        Object.keys(state.pendingRequests).forEach(processId => {
            if (Object.keys(state.pendingRequests[processId]).length > 0) {
                hasRequests = true;
            }
        });
        return hasRequests;
    }

    hasCancelRequests(state) {
        let hasCancelRequests = false;
        Object.keys(state.requestsToCancel).forEach(processId => {
            if (state.requestsToCancel[processId] && state.requestsToCancel[processId].length > 0) {
                hasCancelRequests = true;
            }
        });
        return hasCancelRequests;
    }

    // Should be called once when the app is first started
    startPolling() {
        if (!this.intervalId) {
            this.intervalId = setInterval(this.pollDatastreams, 1000);
        }
    }

    pollDatastreams = () => {
        const state = this.getMyState();
        // Only dispatch if there is something to poll
        if (this.hasPendingRequests(state) || this.hasCancelRequests(state)) {
            this.dispatch({
                type: this.DATASTREAMS_POLL_START,
                pendingRequests: state.pendingRequests,
                reqrequestsToCancel: state.requestsToCancel
            });
        }
    };

    _processDeleteSuccess(action, state) {
        const newState = Object.assign({}, state);
        newState.pendingRequests = Object.assign({}, newState.pendingRequests);
        action.processIds.forEach((processId) => {
            delete newState[processId];
            delete newState.pendingRequests[processId];
            delete newState.requestsToCancel[processId];
        });

        return newState;
    }

    _tabClose(action, state) {
        const newState = Object.assign({}, state);

        action.tabIds.forEach((tab) => {
            const tabObj = TabsService.getObjFromTabText(tab);
            const processId = tabObj.processId;
            delete newState[processId];
        });
        return newState;
    }

    _processRename(action, state) {
        const newState = Object.assign({}, state);
        if (action.oldName in newState) {
            // rename the datastream data
            let value = newState[action.oldName];
            delete newState[action.oldName];
            newState[action.newName] = value;

            // rename the pending requests
            value = newState.pendingRequests[action.oldName];
            delete newState.pendingRequests[action.oldName];
            newState.pendingRequests[action.newName] = value;

            // rename the cancel requests
            value = newState.requestsToCancel[action.oldName];
            delete newState.requestsToCancel[action.oldName];
            newState.requestsToCancel[action.newName] = value;
        }
        return newState;
    }

    _processUncache(action, state) {
        const newState = Object.assign({}, state);
        delete newState[action.processId];
        delete newState.pendingRequests[action.processId];
        delete newState.requestsToCancel[action.processId];
        return newState;
    }

    _datastreamReload(action, state) {
        let newState = Object.assign({}, state);
        const datastreamName = action.datastreamName;
        const processId = action.processId;

        newState[processId] = Object.assign({}, newState[processId]);
        newState[processId][datastreamName] = Object.assign({}, newState[processId][datastreamName],
            {
                currentData: null,
                error: null,
                status: 'Loading'
            }
        );

        // If there is a pending explore request for this datastream, move it list of requests to cancel
        const pendingRequest = this.getPendingRequest(processId, datastreamName, newState);
        if (pendingRequest) {
            newState = this.cancelRequest(pendingRequest, newState);
        }

        return newState;
    }

    _datastreamsExploreSuccess(action, state) {
        // Cache the returned pending requests [ i.e., map of (datastream name -> run dir) ]

        let newState = Object.assign({}, state);
        const processId = action.processId;
        const pendingRequests = action.response;

        newState.pendingRequests[processId] = Object.assign({}, newState.pendingRequests[processId]);
        const datastreamNames = Object.keys(pendingRequests);
        datastreamNames.forEach(datastreamName => {
            const requestId = pendingRequests[datastreamName];
            newState.pendingRequests[processId][datastreamName] = requestId;
        });
        return newState;
    }

    _datastreamsPollError(action, state) {
        const newState = Object.assign({}, state);
        newState.pendingRequests = Object.assign({}, newState.pendingRequests);
        newState.requestsToCancel = Object.assign({}, newState.requestsToCancel);
        const pendingRequests = action.pendingRequests; // multimap of process id to datastream name to run directory
        const requestsToCancel = action.requestsToCancel; // map of process id to array of run directories

        // Mark all the pending requests as an error, since the poll failed, and remove pending request from list
        // TODO: make sure we really want to do this - maybe we should just keep polling?
        Object.keys(pendingRequests).forEach(processId => {
            Object.keys(pendingRequests[processId]).forEach(datastreamName => {
                const dataObject = this.getExploredDatastream(processId, datastreamName, state);
                dataObject.error = 'Failed to execute data explorer web service. See server log for more details.';
                dataObject.status = 'Error';
                newState[processId] = Object.assign({}, newState[processId]);
                newState[processId][datastreamName] = Object.assign({}, dataObject);
                delete newState.pendingRequests[processId][datastreamName];
            });
        });

        // Remove all the cancel requests so we don't keep retrying them
        if (requestsToCancel) {
            Object.keys(requestsToCancel).forEach(processId => {
                this.removeCancelledRequests(processId, requestsToCancel[processId], newState);
            });
        }

        return newState;
    }

    _datastreamsPollSuccess(action, state) {
        let newState = Object.assign({}, state);
        const processId = action.processId;
        const cancelledRequests = action.cancelledRequests;
        const completedRequests = action.completedRequests;
        const datastreamsResponse = action.datastreams;

        // First load the datastream results for completed pending requests
        const datastreamNames = Object.keys(datastreamsResponse);

        for (let i = 0; i < datastreamNames.length; i++) {
            const datastreamName = datastreamNames[i];
            const exploredData = datastreamsResponse[datastreamName];
            const completedRequestId = completedRequests[datastreamName];

            // Only load this data if the requestId is the current pending request (this could be the response from
            // a previous poll where the old request got cancelled)
            const pendingRequestId = this.getPendingRequest(processId, datastreamName, newState);
            if (pendingRequestId  === completedRequestId) {

                const dataObject = this.getExploredDatastream(action.processId, datastreamName, state);
                dataObject.currentData = exploredData;
                dataObject.status = 'Success';

                if (exploredData.exploredFile) {
                    const exploredFiles = dataObject.exploredFiles ? dataObject.exploredFiles : [];
                    exploredFiles.push(exploredData.exploredFile);
                    dataObject.exploredFiles = exploredFiles;
                }

                newState[processId] = Object.assign({}, newState[processId]);
                newState[processId][datastreamName] = Object.assign({}, dataObject);
            }
        }

        // Now remove the cancelled requests from the cancel list
        newState = this.removeCancelledRequests(action.processId, cancelledRequests, newState);

        // Now remove the completed datastreams from the pending requests list
        newState = this.removePendingRequests(action.processId, datastreamNames, newState);

        return newState;
    }

    _datastreamsExploreError(action, state) {
        // This happens if you can't even submit the explore request in the first place

        const newState = Object.assign({}, state);
        const datastreamsRequests = action.requests;
        const processId = action.processId;

        for (let i = 0; i < datastreamsRequests.length; i++) {
            const datastreamName = datastreamsRequests[i].datastreamName;
            const dataObject = this.getExploredDatastream(action.processId, action.datastreamName, state);
            dataObject.error = 'Failed to execute data explorer web service. See server log for more details.';
            dataObject.status = 'Error';
            newState[processId] = Object.assign({}, newState[processId]);
            newState[processId][datastreamName] = Object.assign({}, dataObject);

        }

        return newState;
    }
}

export default new DataExplorerService();



// WEBPACK FOOTER //
// ./src/services/DataExplorerService.js